import {
  deleteV1UsersMeRutterConnectionsId,
  getV1CompaniesCompanyIdLineItemTypes,
  getV1RutterConnections,
  getV1RutterConnectionsConnectionIdCustomersAccounts,
  getV1RutterConnectionsConnectionIdItemMappings,
  type GetV1RutterConnectionsConnectionIdItemMappingsData,
  getV1RutterConnectionsConnectionIdItemRules,
  getV1RutterConnectionsConnectionIdItems,
  type GetV1RutterConnectionsConnectionIdItemsData,
  getV1RutterConnectionsConnectionIdLedgerAccounts,
  type GetV1RutterConnectionsConnectionIdLedgerAccountsData,
  getV1RutterConnectionsConnectionIdLineItemMatches,
  type GetV1RutterConnectionsConnectionIdLineItemMatchesData,
  getV1RutterConnectionsConnectionIdPaymentTerms,
  type GetV1RutterConnectionsConnectionIdPaymentTermsData,
  getV1RutterConnectionsConnectionIdTaxRatesTaxRateSchedules,
  getV1RutterConnectionsConnectionIdVendorsAccounts,
  type GetV1RutterConnectionsData,
  patchV1CompaniesCompanyIdRutterConnectionsId,
  patchV1RutterCustomersId,
  patchV1RutterLedgerAccountsId,
  patchV1RutterLineItemMatchesId,
  patchV1RutterPaymentTermsId,
  patchV1RutterTaxRatesId,
  patchV1RutterVendorsId,
  postV1RutterConnections,
  postV1RutterConnectionsConnectionIdLineItemMatches,
  putV1RutterConnectionsConnectionIdImport,
  putV1RutterConnectionsConnectionIdItemMappings,
  putV1RutterConnectionsConnectionIdItemRules,
  putV1RutterConnectionsConnectionIdItemRulesAddOnGenerate,
  putV1RutterConnectionsConnectionIdItemRulesFreightGenerate,
  putV1RutterConnectionsConnectionIdItemRulesMaterialGenerate,
  putV1RutterConnectionsConnectionIdLineItemMatchesMatch,
  type Rutter_ItemRule_Upsert,
  type RutterItemizableType,
  RutterItemRuleLineKind,
  type RutterLedgerAccountType,
  type SettlementExportSide,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Account } from '~hooks/useAccount/models';
import { TaxRateSchedule } from '~hooks/useTaxRateSchedule/models';
import connection from '~services/connectionModule';
import { extractPagination, Pagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store/RootStore';
import { Nullable } from '~types/Nullable';
import rethrowIfAborted from '~utils/rethrowIfAborted';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  LineItemTypeOption,
  QuickBooksItemLine,
  RutterConnection,
  RutterCustomer,
  RutterItem,
  RutterItemMapping,
  RutterItemRule,
  RutterLedgerAccount,
  RutterLineItemMatch,
  RutterMappedRecord,
  RutterPaymentTerm,
  RutterTaxRate,
  RutterTaxRateMappedRecord,
  RutterVendor,
} from './models';

interface PaginatedOptions {
  pagination: Pagination;
}

interface UpdateConnectionParams {
  sharedWithChildCompanies: boolean;
}

interface MappedRowsPageParams {
  connectionId: string;
  cursor?: string;
  limit: number;
  link?: PaginationLink;
  search?: string;
  signal?: AbortSignal;
}

// A new spec line kind fails type-check here until it gets its generate endpoint
const generateFunctionByLineKind = {
  [RutterItemRuleLineKind.FREIGHT]:
    putV1RutterConnectionsConnectionIdItemRulesFreightGenerate,
  [RutterItemRuleLineKind.MATERIAL]:
    putV1RutterConnectionsConnectionIdItemRulesMaterialGenerate,
  [RutterItemRuleLineKind.ADD_ON]:
    putV1RutterConnectionsConnectionIdItemRulesAddOnGenerate,
} satisfies Record<
  RutterItemRuleLineKind,
  typeof putV1RutterConnectionsConnectionIdItemRulesFreightGenerate
>;

// A company has a handful of connections at most, so the scan almost always ends on the
// first page; the cursor loop only exists so a usable one further back is still found.
const SYNCHRONIZED_CONNECTION_PAGE_LIMIT = 10;

export default function useRutter() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const createConnection = async () => {
    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      const response = await postV1RutterConnections({
        path: { 'company-id': companyId },
        body: {},
      });

      return RutterConnection.parse(response.data.data);
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.rutter.failed_to_create_connection'),
        );
      } catch {
        // Error handling side effects already executed in connection.handleRequestError.
      }

      return null;
    }
  };

  const deleteConnection = async (
    connectionId: string,
    errorCallback?: (error: AxiosError) => void,
  ) => {
    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      await deleteV1UsersMeRutterConnectionsId({
        path: {
          'company-id': companyId,
          id: connectionId,
        },
      });
    } catch (error) {
      if (isAxiosError(error)) {
        errorCallback?.(error);
      }

      connection.handleRequestError(
        error,
        t('error_messages.rutter.failed_to_delete_connection'),
        [500],
      );
    }
  };

  const getConnections = useCallback(
    async (pagination: Pagination, signal?: AbortSignal) => {
      try {
        const companyId = getEffectiveUserCompanyId(userStore);
        const query: NonNullable<GetV1RutterConnectionsData['query']> = {
          'page[limit]': pagination.limit,
        };

        if (pagination.after) {
          query['page[after]'] = pagination.after;
        } else if (pagination.before) {
          query['page[before]'] = pagination.before;
        }

        const response = await getV1RutterConnections({
          path: { 'company-id': companyId },
          query,
          signal,
        });
        const connections = response.data.data.map((rutterConnection) => {
          return RutterConnection.parse(rutterConnection);
        });

        return { data: connections, pagination: extractPagination(response) };
      } catch (error) {
        rethrowIfAborted(error, signal);

        try {
          connection.handleRequestError(
            error,
            t('error_messages.rutter.failed_to_fetch_connections'),
          );
        } catch {
          // Error handling side effects already executed in connection.handleRequestError.
        }

        return null;
      }
    },
    [userStore],
  );

  // Quiet probe used to gate settlement export routing. The scan itself never reports an
  // error: a failure here (for example a user without the manage_accounting_integration
  // permission) must leave the caller free to fall back to the manual path without a
  // toast, so it reports "unknown" as null rather than as "no connection".
  const scanForSynchronizedConnection = useCallback(
    async (signal?: AbortSignal) => {
      try {
        let cursor: string | undefined;
        let hasMore = true;

        while (hasMore) {
          const response = await getV1RutterConnections({
            path: { 'company-id': companyId },
            query: {
              'page[limit]': SYNCHRONIZED_CONNECTION_PAGE_LIMIT,
              ...(cursor ? { 'page[after]': cursor } : {}),
            },
            signal,
          });
          const synchronized = response.data.data.find(
            (rutterConnection) =>
              !!rutterConnection.synchronized_at && !rutterConnection.discarded_at,
          );

          if (synchronized) {
            return { connection: RutterConnection.parse(synchronized) };
          }

          cursor = extractPagination(response).after;
          hasMore = Boolean(cursor);
        }

        return { connection: null };
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        return null;
      }
    },
    [companyId],
  );

  const hasSynchronizedConnection = useCallback(async () => {
    const scan = await scanForSynchronizedConnection();

    return scan ? Boolean(scan.connection) : null;
  }, [scanForSynchronizedConnection]);

  // Same quiet-probe contract as hasSynchronizedConnection, but keeps the connection so a
  // caller can go on to read from it. Returns null when there is nothing usable to read from,
  // including when the user lacks the accounting-integration permission.
  const getSynchronizedConnection = useCallback(
    async (signal?: AbortSignal) => {
      const scan = await scanForSynchronizedConnection(signal);

      return scan?.connection ?? null;
    },
    [scanForSynchronizedConnection],
  );

  // A user who can see a Settlement but not manage the accounting integration gets a 403
  // here. That is an expected outcome for them rather than an error, so a 403 stays quiet
  // and the caller simply renders no terms. Any other failure is genuine and is reported.
  const getPaymentTerms = useCallback(
    async (connectionId: string, pagination: Pagination, signal?: AbortSignal) => {
      try {
        const query: NonNullable<
          GetV1RutterConnectionsConnectionIdPaymentTermsData['query']
        > = {
          'page[limit]': pagination.limit,
        };

        if (pagination.after) {
          query['page[after]'] = pagination.after;
        } else if (pagination.before) {
          query['page[before]'] = pagination.before;
        }

        const response = await getV1RutterConnectionsConnectionIdPaymentTerms({
          path: {
            'company-id': companyId,
            'connection-id': connectionId,
          },
          query,
          signal,
        });
        const paymentTerms = response.data.data.map((paymentTerm) =>
          RutterPaymentTerm.parse(paymentTerm),
        );

        return { data: paymentTerms, pagination: extractPagination(response) };
      } catch (error) {
        rethrowIfAborted(error, signal);

        const userCannotManageTheIntegration =
          isAxiosError(error) && error.response?.status === 403;

        if (!userCannotManageTheIntegration) {
          try {
            connection.handleRequestError(
              error,
              t('error_messages.rutter.failed_to_fetch_payment_terms'),
            );
          } catch {
            // Error handling side effects already executed in connection.handleRequestError.
          }
        }

        return null;
      }
    },
    [companyId],
  );

  // The settlement term picker needs every term to resolve the one a settlement was
  // exported with, so it walks the cursor to exhaustion. Keeps getPaymentTerms' quiet-403
  // contract: null means "no terms to offer", not necessarily a failure.
  const fetchAllPaymentTerms = useCallback(
    async (connectionId: string, signal?: AbortSignal) => {
      const allPaymentTerms: RutterPaymentTerm[] = [];
      let cursor: string | undefined;
      let hasMore = true;

      while (hasMore) {
        const response = await getPaymentTerms(
          connectionId,
          {
            limit: MAX_API_PAGE_SIZE,
            page: 1,
            ...(cursor ? { after: cursor } : {}),
          },
          signal,
        );

        if (!response) {
          return null;
        }

        allPaymentTerms.push(...response.data);
        cursor = response.pagination.after;
        hasMore = Boolean(cursor);
      }

      return allPaymentTerms;
    },
    [getPaymentTerms],
  );

  const updatePaymentTerm = useCallback(
    async (rutterPaymentTermId: string, paymentTermId: string | null) => {
      try {
        const response = await patchV1RutterPaymentTermsId({
          path: { 'company-id': companyId, id: rutterPaymentTermId },
          body: { payment_term_id: paymentTermId },
        });

        return RutterPaymentTerm.parse(response.data.data);
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.rutter.failed_to_update_payment_term'),
          );
        } catch {
          // Error already handled (toast + Sentry); a null return lets the caller
          // restore the row from server truth instead of unwinding the click handler.
        }

        return null;
      }
    },
    [companyId],
  );

  const enqueueImport = async (connectionId: string) => {
    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      await putV1RutterConnectionsConnectionIdImport({
        path: {
          'company-id': companyId,
          'connection-id': connectionId,
        },
      });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.rutter.failed_to_enqueue_import'),
      );
    }
  };

  const getCustomerAccountsPage = useCallback(
    async ({
      connectionId,
      cursor,
      limit,
      link,
      search,
      signal,
    }: MappedRowsPageParams) => {
      try {
        const response = await getV1RutterConnectionsConnectionIdCustomersAccounts({
          path: { 'company-id': companyId, 'connection-id': connectionId },
          query: {
            'page[limit]': limit,
            ...(search ? { 'search[datagrid]': search } : {}),
            ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          },
          signal,
        });

        return {
          data: response.data.data.map((account) => ({
            account: Account.parse(account),
            mappedRecord: account.rutter_customer
              ? RutterMappedRecord.parse(account.rutter_customer)
              : null,
          })),
          pagination: extractPagination(response),
        };
      } catch (error) {
        rethrowIfAborted(error, signal);

        connection.handleRequestError(
          error,
          t('error_messages.account.failed_to_fetch_company_accounts'),
        );
        throw error;
      }
    },
    [companyId],
  );

  const getVendorAccountsPage = useCallback(
    async ({
      connectionId,
      cursor,
      limit,
      link,
      search,
      signal,
    }: MappedRowsPageParams) => {
      try {
        const response = await getV1RutterConnectionsConnectionIdVendorsAccounts({
          path: { 'company-id': companyId, 'connection-id': connectionId },
          query: {
            'page[limit]': limit,
            ...(search ? { 'search[datagrid]': search } : {}),
            ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          },
          signal,
        });

        return {
          data: response.data.data.map((account) => ({
            account: Account.parse(account),
            mappedRecord: account.rutter_vendor
              ? RutterMappedRecord.parse(account.rutter_vendor)
              : null,
          })),
          pagination: extractPagination(response),
        };
      } catch (error) {
        rethrowIfAborted(error, signal);

        connection.handleRequestError(
          error,
          t('error_messages.account.failed_to_fetch_company_accounts'),
        );
        throw error;
      }
    },
    [companyId],
  );

  const getTaxRateSchedulesPage = useCallback(
    async ({
      connectionId,
      cursor,
      limit,
      link,
      search,
      signal,
    }: MappedRowsPageParams) => {
      try {
        const response = await getV1RutterConnectionsConnectionIdTaxRatesTaxRateSchedules(
          {
            path: { 'company-id': companyId, 'connection-id': connectionId },
            query: {
              'page[limit]': limit,
              ...(search ? { 'search[datagrid]': search } : {}),
              ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
            },
            signal,
          },
        );

        return {
          data: response.data.data.map((taxRateSchedule) => ({
            mappedRecord: taxRateSchedule.rutter_tax_rate
              ? RutterTaxRateMappedRecord.parse(taxRateSchedule.rutter_tax_rate)
              : null,
            taxRateSchedule: TaxRateSchedule.parse(taxRateSchedule),
          })),
          pagination: extractPagination(response),
        };
      } catch (error) {
        rethrowIfAborted(error, signal);

        connection.handleRequestError(
          error,
          t('error_messages.tax_rate_schedules.failed_to_fetch'),
        );
        throw error;
      }
    },
    [companyId],
  );

  const updateCustomer = async (
    rutterCustomerId: string,
    customerAccountId: string | null,
  ) => {
    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      const response = await patchV1RutterCustomersId({
        path: {
          'company-id': companyId,
          id: rutterCustomerId,
        },
        body: { customer_account_id: customerAccountId },
      });

      return RutterCustomer.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.rutter.failed_to_update_customer'),
      );

      return null;
    }
  };

  const updateVendor = async (rutterVendorId: string, vendorAccountId: string | null) => {
    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      const response = await patchV1RutterVendorsId({
        path: {
          'company-id': companyId,
          id: rutterVendorId,
        },
        body: { vendor_account_id: vendorAccountId },
      });

      return RutterVendor.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.rutter.failed_to_update_vendor'),
      );

      return null;
    }
  };

  // Items
  const getItems = async (
    connectionId: string,
    pagination: Pagination,
    signal?: AbortSignal,
  ) => {
    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      const query: NonNullable<GetV1RutterConnectionsConnectionIdItemsData['query']> = {
        'page[limit]': pagination.limit,
      };

      if (pagination.after) {
        query['page[after]'] = pagination.after;
      } else if (pagination.before) {
        query['page[before]'] = pagination.before;
      }

      const response = await getV1RutterConnectionsConnectionIdItems({
        path: {
          'company-id': companyId,
          'connection-id': connectionId,
        },
        query,
        signal,
      });
      const items = response.data.data.map((item) => {
        return RutterItem.parse(item);
      });

      return { data: items, pagination: extractPagination(response) };
    } catch (error) {
      rethrowIfAborted(error, signal);

      connection.handleRequestError(
        error,
        t('error_messages.rutter.failed_to_fetch_items'),
      );

      return null;
    }
  };

  // Line item matches
  const getLineItemMatches = useCallback(
    async (
      connectionId: string,
      settlementId: string,
      pagination: Pagination,
      signal?: AbortSignal,
    ) => {
      try {
        const query: GetV1RutterConnectionsConnectionIdLineItemMatchesData['query'] = {
          settlement_id: settlementId,
          'page[limit]': pagination.limit,
          ...(pagination.after ? { 'page[after]': pagination.after } : {}),
        };
        const response = await getV1RutterConnectionsConnectionIdLineItemMatches({
          path: { 'company-id': companyId, 'connection-id': connectionId },
          query,
          signal,
        });

        return {
          data: response.data.data.map(RutterLineItemMatch.parse),
          pagination: extractPagination(response),
        };
      } catch (error) {
        rethrowIfAborted(error, signal);

        return connection.handleRequestError(
          error,
          t('error_messages.rutter.failed_to_fetch_line_item_matches'),
        );
      }
    },
    [companyId],
  );

  const enqueueLineItemMatching = useCallback(
    async (connectionId: string, settlementId: string) => {
      try {
        await putV1RutterConnectionsConnectionIdLineItemMatchesMatch({
          path: { 'company-id': companyId, 'connection-id': connectionId },
          body: { settlement_id: settlementId },
        });

        return true;
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.rutter.failed_to_match_line_items'),
          );
        } catch {
          // Error already handled (toast + Sentry); false lets the caller stop polling.
        }

        return false;
      }
    },
    [companyId],
  );

  const createLineItemMatch = useCallback(
    async (
      connectionId: string,
      line: QuickBooksItemLine,
      rutterItemId: Nullable<string>,
    ) => {
      try {
        const response = await postV1RutterConnectionsConnectionIdLineItemMatches({
          path: { 'company-id': companyId, 'connection-id': connectionId },
          body: {
            add_on_charge_id: line.addOnChargeId,
            rutter_item_id: rutterItemId,
            settlement_line_item_id: line.settlementLineItemId,
          },
        });

        return RutterLineItemMatch.parse(response.data.data);
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.rutter.failed_to_update_line_item_match'),
          );
        } catch {
          // Error already handled (toast + Sentry); null lets the caller restore the row.
        }

        return null;
      }
    },
    [companyId],
  );

  const updateLineItemMatch = useCallback(
    async (matchId: string, rutterItemId: Nullable<string>) => {
      try {
        const response = await patchV1RutterLineItemMatchesId({
          path: { 'company-id': companyId, id: matchId },
          body: { rutter_item_id: rutterItemId },
        });

        return RutterLineItemMatch.parse(response.data.data);
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.rutter.failed_to_update_line_item_match'),
          );
        } catch {
          // Error already handled (toast + Sentry); null lets the caller restore the row.
        }

        return null;
      }
    },
    [companyId],
  );

  // Item rules
  const getItemRules = useCallback(
    async (connectionId: string, signal?: AbortSignal) => {
      try {
        const response = await getV1RutterConnectionsConnectionIdItemRules({
          path: { 'company-id': companyId, 'connection-id': connectionId },
          signal,
        });

        return response.data.data.map(RutterItemRule.parse);
      } catch (error) {
        rethrowIfAborted(error, signal);

        return connection.handleRequestError(
          error,
          t('error_messages.rutter.failed_to_fetch_item_rules'),
        );
      }
    },
    [companyId],
  );

  const upsertItemRule = useCallback(
    async (connectionId: string, body: Rutter_ItemRule_Upsert) => {
      try {
        const response = await putV1RutterConnectionsConnectionIdItemRules({
          path: { 'company-id': companyId, 'connection-id': connectionId },
          body,
        });

        return RutterItemRule.parse(response.data.data);
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.rutter.failed_to_save_item_rule'),
          );
        } catch {
          // Error already handled (toast + Sentry); null lets the caller keep the draft.
        }

        return null;
      }
    },
    [companyId],
  );

  const generateItemRule = useCallback(
    async (
      connectionId: string,
      lineKind: RutterItemRuleLineKind,
      side: SettlementExportSide,
    ) => {
      try {
        await generateFunctionByLineKind[lineKind]({
          path: { 'company-id': companyId, 'connection-id': connectionId },
          body: { side },
        });

        return true;
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.rutter.failed_to_suggest_item_rules'),
          );
        } catch {
          // Error already handled (toast + Sentry); false lets the caller stop waiting.
        }

        return false;
      }
    },
    [companyId],
  );

  const getItemMappings = async (
    connectionId: string,
    pagination: Pagination,
    signal?: AbortSignal,
  ) => {
    try {
      const query: NonNullable<
        GetV1RutterConnectionsConnectionIdItemMappingsData['query']
      > = {
        'page[limit]': pagination.limit,
      };

      if (pagination.after) {
        query['page[after]'] = pagination.after;
      } else if (pagination.before) {
        query['page[before]'] = pagination.before;
      }

      const response = await getV1RutterConnectionsConnectionIdItemMappings({
        path: {
          'company-id': companyId,
          'connection-id': connectionId,
        },
        query,
        signal,
      });
      const itemMappings = response.data.data.map((itemMapping) => {
        return RutterItemMapping.parse(itemMapping);
      });

      return { data: itemMappings, pagination: extractPagination(response) };
    } catch (error) {
      rethrowIfAborted(error, signal);

      connection.handleRequestError(
        error,
        t('error_messages.rutter.failed_to_fetch_item_mappings'),
      );

      return null;
    }
  };

  const upsertItemMapping = async (
    connectionId: string,
    itemizableType: RutterItemizableType,
    itemizableId: string,
    rutterItemId: string | null,
  ) => {
    try {
      const response = await putV1RutterConnectionsConnectionIdItemMappings({
        path: {
          'company-id': companyId,
          'connection-id': connectionId,
        },
        body: {
          rutter_itemizable_type: itemizableType,
          rutter_itemizable_id: itemizableId,
          rutter_item_id: rutterItemId,
        },
      });

      return RutterItemMapping.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.rutter.failed_to_update_item_mapping'),
      );

      return null;
    }
  };

  const getLineItemTypes = async (options: PaginatedOptions, signal?: AbortSignal) => {
    try {
      const response = await getV1CompaniesCompanyIdLineItemTypes({
        path: { 'company-id': companyId },
        query: { 'page[limit]': options.pagination.limit },
        signal,
      });
      const lineItemTypes = response.data.data.map((lineItemType) => {
        return LineItemTypeOption.parse(lineItemType);
      });

      return { data: lineItemTypes, pagination: options.pagination };
    } catch (error) {
      rethrowIfAborted(error, signal);

      connection.handleRequestError(
        error,
        t('error_messages.rutter.failed_to_fetch_line_item_types'),
      );

      return null;
    }
  };

  // Ledger
  const getLedgerAccounts = async (
    connectionId: string,
    pagination: Pagination,
    signal?: AbortSignal,
  ) => {
    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      const query: NonNullable<
        GetV1RutterConnectionsConnectionIdLedgerAccountsData['query']
      > = {
        'page[limit]': pagination.limit,
      };

      if (pagination.after) {
        query['page[after]'] = pagination.after;
      } else if (pagination.before) {
        query['page[before]'] = pagination.before;
      }

      const response = await getV1RutterConnectionsConnectionIdLedgerAccounts({
        path: {
          'company-id': companyId,
          'connection-id': connectionId,
        },
        query,
        signal,
      });
      const items = response.data.data.map((item) => {
        return RutterLedgerAccount.parse(item);
      });

      return { data: items, pagination: extractPagination(response) };
    } catch (error) {
      rethrowIfAborted(error, signal);

      connection.handleRequestError(
        error,
        t('error_messages.rutter.failed_to_fetch_ledger'),
      );

      return null;
    }
  };

  // unlike the other update functions this one propagates errors: the ledger
  // selector inspects the 422 response to show the dedicated "type already taken"
  // copy inline, so swallowing here would reduce it to a generic message
  const updateLedgerAccount = async (
    rutterLedgerAccountId: string,
    type: RutterLedgerAccountType | null,
  ) => {
    const companyId = getEffectiveUserCompanyId(userStore);
    const response = await patchV1RutterLedgerAccountsId({
      path: {
        'company-id': companyId,
        id: rutterLedgerAccountId,
      },
      body: { type },
    });

    return RutterLedgerAccount.parse(response.data.data);
  };

  const updateConnection = useCallback(
    async (
      connectionId: string,
      { sharedWithChildCompanies }: UpdateConnectionParams,
    ) => {
      try {
        const response = await patchV1CompaniesCompanyIdRutterConnectionsId({
          path: { 'company-id': companyId, id: connectionId },
          body: { shared_with_child_companies: sharedWithChildCompanies },
        });

        return RutterConnection.parse(response.data.data);
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.rutter.failed_to_update_connection'),
          );
        } catch {
          // Error already handled (toast + Sentry); null lets the caller revert the toggle.
        }

        return null;
      }
    },
    [companyId],
  );

  const updateTaxRate = useCallback(
    async (rutterTaxRateId: string, taxRateScheduleId: string | null) => {
      try {
        const companyId = getEffectiveUserCompanyId(userStore);
        const response = await patchV1RutterTaxRatesId({
          path: {
            'company-id': companyId,
            id: rutterTaxRateId,
          },
          body: { tax_rate_schedule_id: taxRateScheduleId },
        });

        return RutterTaxRate.parse(response.data.data);
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.rutter.failed_to_update_tax_rate'),
          );
        } catch {
          // Error already handled (toast + Sentry); a null return lets the caller
          // restore the row from server truth instead of unwinding the click handler.
        }

        return null;
      }
    },
    [userStore],
  );

  return {
    hasSynchronizedConnection,
    createConnection,
    createLineItemMatch,
    deleteConnection,
    enqueueImport,
    enqueueLineItemMatching,
    generateItemRule,
    getConnections,
    getCustomerAccountsPage,
    getItemMappings,
    getItemRules,
    getItems,
    getLedgerAccounts,
    getLineItemMatches,
    getLineItemTypes,
    fetchAllPaymentTerms,
    getPaymentTerms,
    getSynchronizedConnection,
    getTaxRateSchedulesPage,
    getVendorAccountsPage,
    updateConnection,
    updateCustomer,
    updateLedgerAccount,
    updateLineItemMatch,
    updatePaymentTerm,
    updateTaxRate,
    updateVendor,
    upsertItemMapping,
    upsertItemRule,
  };
}
